import { Assert } from '@cotera/utilities';
import { BaseModel } from '../actions/framework';
import { DG } from '@cotera/sdk/core';
import { TargetableModel } from '../actions/framework/types';
import {
  ReadonlyWatchable,
  StaleState,
  Watchable,
} from '../actions/framework/watchable';
import { err, ok, Result } from 'neverthrow';
import { v4 } from 'uuid';
import { Routes } from '../routes';

export class DocumentModel extends BaseModel implements TargetableModel {
  private _isTargeted: boolean = false;

  readonly formState: Watchable<{
    documentState: DG.DocumentEditor.DocumentState;
  }>;

  readonly errors: ReadonlyWatchable<DG.Graph.GraphError[]>;
  readonly isEditorStale: ReadonlyWatchable<StaleState>;
  readonly hasBeenTargeted: Watchable<boolean>;
  readonly changes: ReadonlyWatchable<Record<string, boolean>>;
  readonly internalCompiler: ReadonlyWatchable<DG.GraphCompiler>;
  readonly graph: ReadonlyWatchable<DG.BuildExplorer>;
  readonly newOperations = Watchable.fromValue<DG.OperationData[]>([]);
  readonly pageTitle: ReadonlyWatchable<string>;
  readonly storedFormState: ReadonlyWatchable<{
    documentState: DG.DocumentEditor.DocumentState;
  }>;
  /**
   * the UI name state - editable by the user
   */
  readonly editorState: ReadonlyWatchable<DG.DocumentEditor.EditorState>;
  readonly route: ReadonlyWatchable<string>;
  /**
   * The saved name in the base graph - updated when operations are staged
   * This is the source of truth for the document's name in the graph
   */
  readonly currentNameInGraph: Watchable<string | null>;
  readonly name: ReadonlyWatchable<string>;
  /**
   * the identity in the graph - null if not exists yet
   */
  readonly documentIdentity: ReadonlyWatchable<DG.Graph.Identity | null>;

  constructor(
    private readonly inputs: {
      name: string;
      graph: ReadonlyWatchable<DG.BuildExplorer>;
      compiler: ReadonlyWatchable<DG.GraphCompiler>;
    },
    readonly opts: {
      id?: string;
      targeted?: boolean;
      isNew?: boolean;
    } = {}
  ) {
    super(opts.id ?? inputs.name);

    this._isTargeted = opts.targeted ?? false;

    this.storedFormState = Watchable.from((get) => {
      const documentNode = get(inputs.graph).nodeMap[inputs.name];
      if (opts.isNew || documentNode === undefined) {
        return {
          documentState: { type: 'doc' as const, content: [] },
        };
      }
      Assert.assert(documentNode.t === 'document');
      return {
        documentState: documentNode.source ?? {
          type: 'doc' as const,
          content: [],
        },
      };
    });

    this.formState = Watchable.fromValue(this.storedFormState.snapshot());

    this.editorState = Watchable.from((get) => {
      return new DG.DocumentEditor.EditorState(
        get(this.formState).documentState
      );
    });

    // Initialize currentNameInGraph with the name from the base graph
    // This will be updated when operations are staged via markAsStaged()
    this.currentNameInGraph = Watchable.fromValue<string | null>(
      opts.isNew ? null : inputs.name
    );

    this.name = Watchable.from((get) => {
      return get(this.editorState).title() ?? 'New Document';
    });

    this.pageTitle = Watchable.from((get) => {
      return get(this.name);
    });

    this.route = Watchable.from((get) => {
      return Routes.document.forId(get(this.name)).toString();
    });

    this.internalCompiler = Watchable.from((get) => {
      const baseCompiler = get(inputs.compiler);

      return baseCompiler.commit({
        actorId: v4(),
        message: null,
        operations: [...get(this.newOperations)],
        timestamp: new Date(),
      });
    });

    this.graph = Watchable.from((get) => {
      return get(this.internalCompiler)
        .compile()
        .match(
          (x) => x,
          (e) => e.lastGoodBuild
        );
    });

    this.documentIdentity = Watchable.from((get) => {
      const savedName = get(this.currentNameInGraph);
      if (savedName === null) {
        return null;
      }

      // Look up in the BASE graph (without new operations) using the saved name
      const baseGraph = get(inputs.graph);
      const documentNode = baseGraph.nodeMap[savedName];

      if (documentNode === undefined) {
        return null;
      }
      if (documentNode.t !== 'document') {
        return null;
      }
      return documentNode.meta.identity;
    });

    this.errors = Watchable.from((get) => {
      const sym = get(this.graph).forSymbol(get(this.name));

      if (sym === null) {
        return [];
      }

      return sym.builtNode.match(
        () => [],
        (e) => [e.err]
      );
    });

    this.hasBeenTargeted = Watchable.fromValue(false);

    this.changes = Watchable.from((get) => {
      const editorState = get(this.editorState);
      const initialAgentState = new DG.DocumentEditor.EditorState(
        get(this.storedFormState).documentState
      );
      const graph = get(inputs.graph);

      const currentName = get(this.currentNameInGraph);
      const formName = get(this.name);
      return {
        name: currentName !== null && currentName !== formName,
        document:
          editorState.toPromptMarkdown(graph) !==
          initialAgentState.toPromptMarkdown(graph),
      };
    });
  }

  get isTargeted() {
    return this._isTargeted;
  }

  markAsStaged(): void {
    // Update the saved name to the current name now that changes are staged
    this.currentNameInGraph.set(this.name.snapshot());
    // Clear new operations since they've been staged
    this.newOperations.set([]);
  }

  target(): void {
    this._isTargeted = true;
    this.hasBeenTargeted.set(true);
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  compile(
    props: {
      setOperations?: boolean;
    } = { setOperations: true }
  ): Result<
    { t: 'document' },
    | { t: 'build'; e: DG.CommitsBuildFailure | { t: string; message: string } }
    | { t: 'tc'; e: DG.Graph.GraphError }
  > {
    const graphSnapshot = this.inputs.graph.snapshot();
    const compiler = graphSnapshot.compiler;

    const nextOps = this.makeOpsFromState();

    if (nextOps.isErr()) {
      return err({ t: 'build', e: nextOps.error });
    }

    const compilationResult = compiler
      .commit({
        actorId: v4(),
        message: null,
        operations: [...nextOps.value],
        timestamp: new Date(),
      })
      .compile();

    if (compilationResult.isErr()) {
      return err({ t: 'build', e: compilationResult.error.err });
    }

    const symbol = compilationResult.value.forSymbol(this.name.snapshot());

    Assert.assert(symbol !== null);

    if (symbol.builtNode.isErr()) {
      return err({ t: 'tc', e: symbol.builtNode.error.err });
    }

    if (props.setOperations) {
      this.newOperations.set(nextOps.value);
    }

    return ok({
      t: 'document',
    });
  }

  private makeOpsFromState(): Result<
    DG.OperationData[],
    { t: string; message: string }
  > {
    const name = this.name.snapshot();
    const { documentState } = this.formState.snapshot();

    if (this.documentIdentity.snapshot() === null) {
      // Create the document node
      return ok([
        DG.Operations.createDocument({
          symbol: name,
          source: documentState,
        }),
      ]);
    }

    const ops: DG.OperationData[] = [...this.newOperations.snapshot()];

    const nameHasChanged = name !== this.currentNameInGraph.snapshot();

    // TODO: implement proper document change detection
    // For now, always update when we're saving
    const documentHasChanged = true;

    const docSymbol = this.graph
      .snapshot()
      .forSymbol(this.currentNameInGraph.snapshot()!);

    if (docSymbol === null) {
      return err({
        t: 'document-not-found',
        message: `Document not found: ${this.currentNameInGraph.snapshot()!}`,
      });
    }

    if (nameHasChanged && !this.isNew) {
      const renameOps = docSymbol.makeRenameOps({
        to: name,
      });

      if (renameOps.isErr()) {
        return err({
          t: 'rename-failure',
          message: JSON.stringify(renameOps.error),
        });
      }
      ops.push(...renameOps.value);
    }

    if (documentHasChanged) {
      const updateOps = docSymbol.makeUpdateOps({
        symbol: name, // Use the NEW name (after rename if applicable)
        source: documentState,
      });

      if (updateOps.isErr()) {
        return err({ t: 'update-failure', message: updateOps.error });
      }

      ops.push(...updateOps.value);
    }

    return ok(ops);
  }

  get isNew() {
    return this.currentNameInGraph.snapshot() === null;
  }
}
